/**
 * Low-level input for a single profile field.
 */
import { View } from "react-native";

import { EnumSelect } from "@/components/ui/enum-select";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { SetSelect } from "@/components/ui/set-select";
import { TEST_IDS } from "@/testIdDefinition";
import { router } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Text } from "../../../components/ui/text";
import { MIDDLE_NAME_FIELD_CODE } from "../config/profileFormGroups";
import { useParentSelectOptions } from "../lib/getParentSelectOptions";
import type {
    ProfileField,
    ProfileFieldInputProps,
} from "../types/profileFieldInput";
import { ProfileFieldLabel } from "./ProfileFieldLabel";
import { ProfileFieldReadOnlyValue } from "./ProfileFieldReadOnlyValue";

export function ProfileFieldInput({
    pipedriveId,
    field,
    value,
    onChange,
    readOnly = false,
}: ProfileFieldInputProps) {
    const isReadOnly = readOnly || field.read_only;
    const parentOptions = useParentSelectOptions();
    const isPeopleField = field.field_type === "people";
    const selectValue = typeof value === "number" ? value : null;
    const peopleLabel =
        selectValue == null
            ? null
            : (parentOptions.find((option) => option.id === selectValue)
                  ?.label ?? null);

    function handlePress() {
        router.push(`/profile/${pipedriveId}/field/${field.field_code}`);
    }

    return (
        <View
            className="px-3 py-3"
            testID={TEST_IDS[TEST_IDS.PROFILE_FIELD_ROW]}
        >
            <View className="flex-row items-baseline justify-between gap-2">
                <ProfileFieldLabel isReadOnly={isReadOnly}>
                    {field.field_name}
                </ProfileFieldLabel>
                {isReadOnly && <ProfileFieldDescription field={field} />}
            </View>

            {isReadOnly ? (
                <ProfileFieldReadOnlyValue
                    text={isPeopleField ? peopleLabel : field.text}
                />
            ) : field.field_type === "enum" ? (
                <EnumSelect
                    options={field.options ?? []}
                    value={selectValue}
                    onPress={handlePress}
                    testID={TEST_IDS[TEST_IDS.PROFILE_FIELD_SELECT]}
                />
            ) : isPeopleField ? (
                <EnumSelect
                    options={parentOptions}
                    value={selectValue}
                    onPress={handlePress}
                    testID={TEST_IDS[TEST_IDS.PROFILE_FIELD_SELECT]}
                />
            ) : field.field_type === "set" ? (
                <SetSelect
                    options={field.options ?? []}
                    value={Array.isArray(value) ? value : []}
                    onPress={handlePress}
                    testID={TEST_IDS[TEST_IDS.PROFILE_FIELD_SELECT]}
                />
            ) : (
                <Input
                    testID={TEST_IDS[TEST_IDS.PROFILE_FIELD_INPUT]}
                    value={
                        typeof value === "string"
                            ? value
                            : value == null
                              ? ""
                              : String(value)
                    }
                    onChangeText={(nextValue) => onChange(nextValue)}
                    keyboardType={
                        field.field_code === "email"
                            ? "email-address"
                            : field.field_code === "phone"
                              ? "phone-pad"
                              : "default"
                    }
                    autoCapitalize={
                        field.field_code === "email" ? "none" : "sentences"
                    }
                    autoComplete={
                        isNameField(field.field_code) ? "off" : undefined
                    }
                    textContentType={
                        isNameField(field.field_code) ? "none" : undefined
                    }
                    importantForAutofill={
                        isNameField(field.field_code) ? "no" : undefined
                    }
                />
            )}
        </View>
    );
}

function isNameField(fieldCode: string): boolean {
    return (
        fieldCode === "first_name" ||
        fieldCode === "last_name" ||
        fieldCode === MIDDLE_NAME_FIELD_CODE
    );
}

function ProfileFieldDescription({ field }: { field: ProfileField }) {
    const insets = useSafeAreaInsets();
    const contentInsets = {
        top: insets.top,
        bottom: insets.bottom,
        left: 12,
        right: 12,
    };
    return (
        field.description && (
            <HoverCard>
                <HoverCardTrigger asChild>
                    <Text variant="caption" className="text-muted-foreground">
                        <Icon
                            name="circle-question"
                            size={14}
                            className="text-muted-foreground"
                        />
                    </Text>
                </HoverCardTrigger>
                <HoverCardContent insets={contentInsets} className="w-80">
                    <View className="flex-1 gap-1">
                        <Text variant="label">{field.field_name}</Text>
                        <Text variant="caption">{field.description}</Text>
                    </View>
                </HoverCardContent>
            </HoverCard>
        )
    );
}
